import { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { ArrowLeft, Download, FileWarning, Loader2 } from 'lucide-react'
import { Button, Card } from '@/components/ui'
import { TrainerReviewResultSummary } from '@/components/pedagogy/TrainerReviewResultSummary'
import { renderFormattedComment } from '@/lib/commentFormatting'
import { TRAINER_CHECKLIST_SECTIONS } from '@/lib/trainerChecklist'
import { pedagogyPaths } from '@/lib/pedagogyRoutes'
import {
  createTrainerChecklistReviewLink,
  formatTrainerReviewDateTime,
  type TrainerChecklistPhoto,
} from '@/lib/trainerChecklistReviews'
import {
  getMyTrainerReviewEntryDetail,
  getTrainerReviewDetail,
  type TrainerReviewDetail,
} from '@/services/trainerReviewService'

/**
 * ⚠️ Все `bg-white` в этом файле — намеренные литералы, а не пропущенные места
 * миграции на тёмную тему. Это печатная форма: `.report-print-page` в
 * src/index.css принудительно возвращает палитру к светлым значениям, поэтому
 * лист отчёта обязан быть белым в любой теме — и на печати, и при просмотре на
 * экране. Переводить их на `bg-card` нельзя: при тёмной теме страница
 * перестала бы выглядеть листом бумаги. То же относится к `text-slate-*`
 * рядом с ними — под печатью лестница уже светлая.
 */

function formatIsoDate(value: string): string {
  const match = value.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (!match) return ''
  return `${match[3]}.${match[2]}.${match[1]}`
}

function sanitizeFileNamePart(value: string): string {
  return value
    .replace(/[\\/:*?"<>|]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

function buildReportFileName(detail: TrainerReviewDetail): string {
  const coach = sanitizeFileNamePart(detail.meta.coachName || 'Без педагога')
  const city = sanitizeFileNamePart(detail.meta.city || 'Без города')
  const direction = sanitizeFileNamePart(detail.meta.directionName || 'Без направления')
  const trainingDate = detail.meta.trainingDateTime
    ? formatIsoDate(detail.meta.trainingDateTime)
    : formatIsoDate(detail.meta.reviewDate)

  return `Отсмотр - ${coach} (${city}) - ${trainingDate || 'без даты'} - ${direction}`
}

function collectAllPhotos(detail: TrainerReviewDetail): TrainerChecklistPhoto[] {
  return [
    ...detail.generalPhotos,
    ...Object.values(detail.criterionPhotos).flat(),
  ]
}

async function fetchPhotoObjectUrl(photo: TrainerChecklistPhoto): Promise<string> {
  const response = await fetch(photo.previewUrl)
  if (!response.ok) {
    throw new Error(`Не удалось загрузить фото ${photo.photoId ?? photo.id}`)
  }

  const blob = await response.blob()
  return URL.createObjectURL(blob)
}

async function waitForImage(url: string): Promise<void> {
  await new Promise<void>((resolve, reject) => {
    const image = new Image()
    image.onload = () => resolve()
    image.onerror = () => reject(new Error('Image failed to load'))
    image.src = url
  })
}

function getCriterionPhotos(
  detail: TrainerReviewDetail,
  criterionCode: string
): TrainerChecklistPhoto[] {
  return detail.criterionPhotos[criterionCode] ?? []
}

function getItemScoreTone(score: number | null | undefined, maxScore: number): string {
  if (score == null || maxScore <= 0) {
    return 'border-slate-300 bg-white text-slate-700'
  }

  if (score <= 0) {
    return 'border-rose-200 bg-rose-50 text-rose-700'
  }

  if (score >= maxScore) {
    return 'border-emerald-200 bg-emerald-50 text-emerald-700'
  }

  return 'border-amber-200 bg-amber-50 text-amber-700'
}

export function TrainerChecklistReportPage() {
  const { reviewId = '' } = useParams()
  const location = useLocation()
  const navigate = useNavigate()
  const [detail, setDetail] = useState<TrainerReviewDetail | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({})
  const [photosReady, setPhotosReady] = useState(false)
  const hasAutoPrintedRef = useRef(false)
  const returnTo = useMemo(() => new URLSearchParams(location.search).get('returnTo') || '', [location.search])
  const shouldAutoPrint = useMemo(() => new URLSearchParams(location.search).get('autoPrint') !== '0', [location.search])

  useEffect(() => {
    document.body.classList.add('report-print-page')
    document.documentElement.classList.add('report-print-page')
    return () => {
      document.body.classList.remove('report-print-page')
      document.documentElement.classList.remove('report-print-page')
    }
  }, [])

  useEffect(() => {
    if (!detail) return

    const previousTitle = document.title
    document.title = buildReportFileName(detail)

    return () => {
      document.title = previousTitle
    }
  }, [detail])

  useEffect(() => {
    let cancelled = false

    async function loadReport() {
      if (!reviewId) {
        setError('Не указан отсмотр для выгрузки.')
        setLoading(false)
        return
      }

      setLoading(true)
      setError('')

      try {
        // Реестр специалиста отдаёт случайные UUID без признака хранилища,
        // общий реестр — числовой review_id. По форме параметра и выбирается
        // источник: личная печатная форма умеет и то и другое, общая —
        // по-прежнему только отсмотры.
        const nextDetail = /^\d+$/.test(reviewId)
          ? await getTrainerReviewDetail(reviewId)
          : await getMyTrainerReviewEntryDetail(reviewId)
        if (cancelled) return

        if (!nextDetail) {
          setError('Сохранённый отсмотр не найден.')
          setDetail(null)
          return
        }

        setDetail(nextDetail)
      } catch (loadError) {
        if (cancelled) return
        console.error('Failed to load trainer review report:', loadError)
        setError(loadError instanceof Error ? loadError.message : 'Не удалось загрузить отчёт')
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    void loadReport()
    return () => {
      cancelled = true
    }
  }, [reviewId])

  useEffect(() => {
    if (!detail) {
      setPhotoUrls({})
      setPhotosReady(false)
      return
    }

    let cancelled = false
    const createdUrls: string[] = []

    async function preparePhotos() {
      const allPhotos = collectAllPhotos(detail)
      if (allPhotos.length === 0) {
        setPhotoUrls({})
        setPhotosReady(true)
        return
      }

      setPhotosReady(false)

      const nextUrls: Record<string, string> = {}

      await Promise.all(allPhotos.map(async photo => {
        try {
          const objectUrl = await fetchPhotoObjectUrl(photo)
          createdUrls.push(objectUrl)
          nextUrls[photo.id] = objectUrl
          await waitForImage(objectUrl)
        } catch (photoError) {
          console.error('Failed to preload report photo:', photoError)
          nextUrls[photo.id] = photo.previewUrl
          try {
            await waitForImage(photo.previewUrl)
          } catch (fallbackError) {
            console.error('Failed to preload fallback report photo:', fallbackError)
          }
        }
      }))

      if (cancelled) return
      setPhotoUrls(nextUrls)
      setPhotosReady(true)
    }

    void preparePhotos()

    return () => {
      cancelled = true
      createdUrls.forEach(url => URL.revokeObjectURL(url))
    }
  }, [detail])

  useEffect(() => {
    if (!shouldAutoPrint || loading || error || !detail || !photosReady || hasAutoPrintedRef.current) return

    hasAutoPrintedRef.current = true
    const timerId = window.setTimeout(() => {
      window.print()
    }, 250)

    return () => window.clearTimeout(timerId)
  }, [detail, error, loading, photosReady, shouldAutoPrint])

  const handlePrint = () => {
    window.print()
  }

  const reportSections = useMemo(() => {
    if (!detail) return []

    return TRAINER_CHECKLIST_SECTIONS
      .map(section => ({
        ...section,
        items: section.items
          .map(item => ({
            ...item,
            score: detail.scores[item.id],
            comment: detail.comments[item.id]?.trim() ?? '',
            photos: getCriterionPhotos(detail, item.id),
          }))
          .filter(item => item.score != null),
      }))
      .filter(section => section.items.length > 0)
  }, [detail])


  if (loading) {
    return (
      <div className="space-y-4 pb-8">
        <Card className="border border-border shadow-sm">
          <div className="text-sm text-muted-foreground">Подготавливаем отчёт...</div>
        </Card>
      </div>
    )
  }

  if (error || !detail) {
    return (
      <div className="space-y-4 pb-8">
        <div className="report-screen-actions flex flex-wrap items-center gap-2">
          <Button type="button" size="sm" variant="outline" onClick={() => returnTo ? navigate(returnTo) : navigate(-1)}>
            <ArrowLeft className="mr-1.5 inline h-4 w-4" />
            Назад
          </Button>
        </div>
        <Card className="border border-error/30 bg-error/5 shadow-sm">
          <div className="flex items-start gap-3 text-sm text-error">
            <FileWarning className="mt-0.5 h-4 w-4 shrink-0" />
            <div>{error || 'Не удалось открыть отчёт.'}</div>
          </div>
        </Card>
      </div>
    )
  }

  return (
    <div className="trainer-review-report min-h-screen space-y-4 bg-white pb-8">
      <div className="report-screen-actions flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            size="sm"
            variant="outline"
            onClick={() => {
              if (returnTo) {
                navigate(returnTo)
                return
              }
              const cardLink = /^\d+$/.test(detail.reviewId)
                ? createTrainerChecklistReviewLink(detail.reviewId)
                : `${pedagogyPaths.reviewNew}?entryKey=${encodeURIComponent(detail.reviewId)}`
              navigate(cardLink)
            }}
          >
            <ArrowLeft className="mr-1.5 inline h-4 w-4" />
            {returnTo ? 'Назад' : 'К карточке'}
          </Button>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {!photosReady && (
            <div className="inline-flex items-center gap-2 rounded-lg border border-border bg-background px-3 py-2 text-xs text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Загружаем фото для PDF
            </div>
          )}
          <Button type="button" size="sm" onClick={handlePrint} disabled={!photosReady}>
            <Download className="mr-1.5 inline h-4 w-4" />
            Скачать PDF
          </Button>
        </div>
      </div>

      <section className="report-sheet mx-auto max-w-5xl space-y-4 bg-white p-0">
        <div className="overflow-hidden rounded-[28px] border border-slate-200/90 bg-white shadow-[0_10px_30px_rgba(15,23,42,0.05)]">
          <div className="h-2 bg-gradient-to-r from-amber-500 via-orange-400 to-emerald-500" />
          <div className="p-5 sm:p-6">
            <div className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-500">Отсмотр тренировки</div>
            <h1 className="mt-3 text-3xl font-bold tracking-tight text-slate-900">Отчёт по тренировке</h1>

            <div className="report-meta-grid mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)_240px] lg:items-stretch">
              <ReportMetaCard label="Город" value={detail.meta.city || '-'} className="report-meta-city lg:col-start-1 lg:row-start-1" />
              <ReportMetaCard label="Студия" value={detail.meta.studioAddress || '-'} className="report-meta-studio lg:col-start-2 lg:row-start-1" />
              <ReportMetaCard label="Педагог" value={detail.meta.coachName || '-'} className="report-meta-teacher lg:col-start-1 lg:row-start-2" />
              <ReportMetaCard
                label="Дата и время тренировки"
                value={formatTrainerReviewDateTime(detail.meta.trainingDateTime) || '-'}
                className="report-meta-datetime lg:col-start-2 lg:row-start-2"
              />
              <ReportMetaCard
                label="Направление"
                value={detail.meta.directionName || '-'}
                className="report-meta-direction sm:col-span-2 lg:col-start-1 lg:row-start-3 lg:col-span-2"
              />
              <TrainerReviewResultSummary
                totalScore={detail.totalScore}
                maxScore={detail.maxScore}
                qualityPercent={detail.qualityPercent}
                className="report-result-summary sm:col-span-2 lg:col-start-3 lg:row-start-2 lg:row-span-2"
              />
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {reportSections.map(section => (
            <section key={section.id} className="report-section space-y-3 rounded-2xl border border-slate-300 bg-slate-50/35 p-4 shadow-[0_1px_0_rgba(15,23,42,0.04)]">
              <div className="report-section-header border-b border-slate-300 pb-3">
                <div className="flex items-center gap-3">
                  <span className="h-8 w-2 rounded-full bg-amber-500" />
                  <h2 className="text-lg font-semibold text-slate-900">{section.title}</h2>
                </div>
              </div>

              <div className="report-section-items space-y-3">
                {section.items.map(item => (
                  <article key={item.id} className="report-item rounded-xl border border-slate-300 bg-white p-4 shadow-[0_1px_0_rgba(15,23,42,0.03)]">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0">
                        <h3 className="text-sm font-semibold leading-6 text-slate-900">{item.label}</h3>
                      </div>
                      <div className={`shrink-0 rounded-full border px-3 py-1.5 text-sm font-semibold tabular-nums ${getItemScoreTone(item.score, item.maxScore)}`}>
                        {item.score == null ? 'Не оценено' : `${item.score} / ${item.maxScore}`}
                      </div>
                    </div>

                    {item.comment && (
                      <div className="mt-3 rounded-xl border border-slate-200 bg-slate-50/40 px-3 py-3 text-sm leading-6 text-slate-600">
                        {renderFormattedComment(item.comment)}
                      </div>
                    )}

                    {item.photos.length > 0 && (
                      <ReportPhotoSection photos={item.photos} photoUrls={photoUrls} />
                    )}
                  </article>
                ))}
              </div>
            </section>
          ))}
        </div>

        {(detail.generalComment.trim() || detail.generalPhotos.length > 0) && (
          <section className="report-section report-section-separate-page space-y-4 rounded-2xl border border-slate-300 bg-slate-50/35 p-4 shadow-[0_1px_0_rgba(15,23,42,0.04)]">
            <div className="report-section-header border-b border-slate-300 pb-3">
              <div className="flex items-center gap-3">
                <span className="h-8 w-2 rounded-full bg-emerald-500" />
                <h2 className="text-lg font-semibold text-slate-900">Общие комментарии и фото</h2>
              </div>
            </div>

            {detail.generalComment.trim() && (
              <div className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm leading-6 text-slate-700">
                {renderFormattedComment(detail.generalComment.trim())}
              </div>
            )}

            {detail.generalPhotos.length > 0 && (
              <ReportPhotoSection photos={detail.generalPhotos} photoUrls={photoUrls} />
            )}
          </section>
        )}
      </section>
    </div>
  )
}

function ReportMetaCard({
  label,
  value,
  className = '',
}: {
  label: string
  value: string
  className?: string
}) {
  return (
    <div className={`rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm ${className}`}>
      <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500">{label}</div>
      <div className="mt-1.5 text-sm font-medium leading-6 text-slate-900">{value}</div>
    </div>
  )
}

function ReportPhotoSection({
  photos,
  photoUrls,
}: {
  photos: TrainerChecklistPhoto[]
  photoUrls: Record<string, string>
}) {
  return (
    <section className="mt-4">
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-3">
        {photos.map(photo => (
          <figure key={photo.id} className="overflow-hidden rounded-2xl border border-slate-300 bg-white shadow-sm">
            <div className="aspect-[4/3] bg-slate-100">
              <img
                src={photoUrls[photo.id] ?? photo.previewUrl}
                alt="Фото отсмотра"
                className="h-full w-full object-cover"
                loading="eager"
              />
            </div>
          </figure>
        ))}
      </div>
    </section>
  )
}
